import React, { useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import style from './style.module.scss';
import SubjectMonitoringDataTables from './subject-monitoring-data-tables';
import Button from '@components-clean/core/button';
import Card from '@components-clean/core/card';
import ErrorMessage from '@components-clean/core/error';
import { InfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import { useOnValueChange } from '@hooks-clean/index';
import {
    SetDataListingReviewedDto,
    SubjectDataListingReviewStatusTypes,
} from '@interfaces-clean/subject-monitoring/subject-data-listing-review';
import { ISMDataset } from '@pages-clean/central-monitoring/interfaces/context';
import AddSubjectNoteDialog from '@pages-clean/subject-monitoring/components/dialogs/add-subject-note-dialog';
import { SMActiveReviewContext } from '@pages-clean/subject-monitoring/components/monitoring-toolkit/active-review-tab/context';
import SubjectProfileService from '@pages-clean/subject-monitoring/subject-profile/subject-profile-service';
import { useTranslation } from '@hooks/index';
import { GET_DATA_REVIEW_TABLES } from '@queries/central-monitoring';
import {
    dataReviewTables,
    dataReviewTablesVariables,
    dataReviewTables_OpraQueries_dataReviewTablesForStudy_DataReviewTable,
} from '@queries/generated/central-monitoring.generated';
import {
    dataListingsConfigTableForStudy,
    dataListingsConfigTableForStudyVariables,
} from '@queries/generated/subject-monitoring.generated';
import { GET_SUBJECT_MONITORING_CONFIGURATION_FOR_TIMESTAMP } from '@queries/subject-monitoring';
import DatasetApi from '@apis/bff-api/central-monitoring/dataset';
import { SubjectDataListingReviewInstanceApi } from '@apis/bff-api/subject-monitoring/subject-data-listing-review-instance';
import { SubjectReviewInstanceApi } from '@apis/bff-api/subject-monitoring/subject-review-instance';
import { SubjectMonitoringReviewAPI } from '@apis/bff-api/subject-monitoring-review';

// Alias to name that isn't awful
type DataReviewTable = dataReviewTables_OpraQueries_dataReviewTablesForStudy_DataReviewTable;

const GROUPED_BY_KEY = 'groupedBy';
const GROUPED_BY_VALUES = {
    ON_REVIEW: 'On Review',
    NOT_ON_REVIEW: 'Not On Review',
} as const;

type Props = {
    studyRef: string;
    studyNumber: string;
    dataset: ISMDataset;
    subjectId: number;
    subjectCode: string;
    subjectNumber: string;
};

const DataListing = ({ studyRef, studyNumber, dataset, subjectId, subjectCode, subjectNumber }: Props) => {
    const timestamp = dataset.created.toISOString();

    const { t: subjectMonitoringTranslation } = useTranslation('sm');
    const { t: centralMonitoringTranslation } = useTranslation('cm');

    const [showAddNoteDialog, setShowAddNoteDialog] = useState(false);

    // If user does not have access to the monitoring toolkit then the review will not be retrieved
    const smActiveReviewContext = useContext(SMActiveReviewContext);
    const isInReviewMode = !!smActiveReviewContext?.state?.reviewRecordId && !!smActiveReviewContext?.state?.sessionId;
    const sessionId = smActiveReviewContext?.state?.sessionId;

    const getReviewQuery = SubjectMonitoringReviewAPI.useGetByRecordIdQuery(
        studyRef,
        smActiveReviewContext?.state.reviewRecordId,
    );

    const review = getReviewQuery.data;

    const getReviewConfigQuery = SubjectMonitoringReviewAPI.useGetConfigForReview(studyRef, review?.recordId);

    const dataListingsOnReviewIdsSet = useMemo(() => {
        if (!getReviewConfigQuery.data) {
            return new Set<string>();
        }

        return new Set(getReviewConfigQuery.data.dataListings.map((x) => x.dataListingId));
    }, [getReviewConfigQuery.data]);

    const getAllDatasetsQuery = DatasetApi.useGetAll(studyRef);

    // Look this up even though we have `dataset` because the dates on `dataset` have timezones...
    const currentDataset = useMemo(() => {
        const matchedDataset = (getAllDatasetsQuery.data ?? []).find((d) => d.id === dataset.id);

        if (matchedDataset == null) {
            return undefined;
        }

        return {
            id: matchedDataset.id as number,
            dataCutDate: new Date(matchedDataset.dataCutDate as Date | string | number),
            created: new Date(matchedDataset.created as Date | string | number),
            isSmEnabledDataset: matchedDataset.isSmEnabledDataset ?? undefined,
        };
    }, [getAllDatasetsQuery.data, dataset.id]);

    const reviewDataset = useMemo(() => {
        const matchedDataset = (getAllDatasetsQuery.data ?? []).find((d) => d.id === review?.datasetId);

        if (matchedDataset == null) {
            return undefined;
        }

        return {
            id: matchedDataset.id as number,
            dataCutDate: new Date(matchedDataset.dataCutDate as Date | string | number),
            created: new Date(matchedDataset.created as Date | string | number),
            isSmEnabledDataset: matchedDataset.isSmEnabledDataset ?? undefined,
        };
    }, [getAllDatasetsQuery.data, review?.datasetId]);

    const getSubjectMonitoringConfigQuery = useQuery<
        dataListingsConfigTableForStudy,
        dataListingsConfigTableForStudyVariables
    >(GET_SUBJECT_MONITORING_CONFIGURATION_FOR_TIMESTAMP, { variables: { studyId: studyRef, timestamp } });

    const getStudyDataTablesQuery = useQuery<dataReviewTables, dataReviewTablesVariables>(GET_DATA_REVIEW_TABLES, {
        variables: { studyId: studyRef, timestamp },
    });

    const dataListingConfigIds = useMemo(() => {
        const subjectDataListingsLower = [
            'SubjectAdverseEvent',
            'SubjectAssessment',
            'SubjectCrf',
            'SubjectConcomitantMedication',
            'SubjectInformedConsent',
            'SubjectIssue',
            'SubjectLab',
            'SubjectMedicalHistory',
            'SubjectMedicalHistories',
            'SubjectProtocolDeviation',
            'SubjectQuery',
            'SubjectQueries',
            'SubjectVisit',
        ].map((y) => y.toLowerCase());
        const studyDataListingIds = new Set(
            (getStudyDataTablesQuery.data?.dataReviewTablesForStudy ?? [])
                .filter((x) => {
                    if (x == null || x.dataSource == null) return false;
                    const source = x.dataSource.toLowerCase(); // lowercase once per item
                    return subjectDataListingsLower.some((y) => source.startsWith(y));
                })
                .map((x) => x!.id),
        );

        const dataListingIdsInConfig = new Set(
            (getSubjectMonitoringConfigQuery.data?.dataListingsConfigTableForStudy?.dataListingsItems ?? [])
                // Ignore nulls
                .filter((x) => x != null)
                .map((dataListingItem) => dataListingItem?.datatableId),
        );

        /**
         * Data listings in config might still include subject-level data listings.
         * The data listings themselves won't show on the FE but the config could still have them,
         * so return the data listing IDs that only exist in both.
         */
        return [...dataListingIdsInConfig.intersection(studyDataListingIds)];
    }, [
        getSubjectMonitoringConfigQuery.data?.dataListingsConfigTableForStudy?.dataListingsItems,
        getStudyDataTablesQuery.data,
    ]);

    // Get which data-tables are available (based on config)
    // Also, if in active-review-mode, group tables based on whether they are/aren't part of the review plan
    const availableDataTables = useMemo<DataReviewTable[]>(() => {
        const configuredDataListingIds = new Set(dataListingConfigIds);
        let tables = (getStudyDataTablesQuery.data?.dataReviewTablesForStudy ?? [])
            // For some reason, it's typed as T | null, so remove nulls...
            .filter((x) => x != null)
            // Only data-tables configured
            .filter((x) => configuredDataListingIds.has(x.id));

        // If not in review mode, just return tables ungrouped
        if (!isInReviewMode) {
            // Wrap in object since `DataTables` component expects the whole query response...
            return tables;
        }

        tables = tables
            .map((t) => ({
                ...t,
                [GROUPED_BY_KEY]: dataListingsOnReviewIdsSet.has(t.id)
                    ? GROUPED_BY_VALUES.ON_REVIEW
                    : GROUPED_BY_VALUES.NOT_ON_REVIEW,
            }))
            // Sort "On Review" first
            .toSorted((a, b) => {
                if (a[GROUPED_BY_KEY] === b[GROUPED_BY_KEY]) {
                    return 0;
                }
                return a[GROUPED_BY_KEY] === GROUPED_BY_VALUES.ON_REVIEW ? -1 : 1;
            });

        // Wrap in object since `DataTables` component expects the whole query response...
        return tables;
    }, [dataListingConfigIds, getStudyDataTablesQuery.data, isInReviewMode, dataListingsOnReviewIdsSet]);

    // This will be defined if the data tables are cached (from being previously fetched)
    // Otherwise, it will be undefined, and the useOnValueChange handler will handle when it changes (e.g. loads)
    const [selectedDatatable, setSelectedDatatable] = useState<DataReviewTable | undefined>(availableDataTables.at(0));

    // When availableDataTables changes, select the first item if:
    // - no data table is selected
    // - the selected data table is not in the new list
    useOnValueChange(availableDataTables, () => {
        const inNewList = availableDataTables.some((x) => x.id === selectedDatatable?.id);

        if (selectedDatatable == null || !inNewList) {
            setSelectedDatatable(availableDataTables.at(0));
        }
    });

    // Last reviewed dataset is specific to the selected data listing, so this must re-fetch when the
    // selected data table changes (hence it lives after `selectedDatatable` is resolved).
    const getLastReviewedDatasetIdForReviewPlanForDataListingForSubjectQuery =
        SubjectMonitoringReviewAPI.useGetLastReviewedDatasetIdForReviewPlanForDataListingForSubjectQuery(
            studyRef,
            review?.reviewPlanId,
            subjectId,
            selectedDatatable?.id,
        );

    const lastReviewedDataset = useMemo(() => {
        const id = getLastReviewedDatasetIdForReviewPlanForDataListingForSubjectQuery.data;
        if (id == null) {
            return undefined;
        }

        const matchedDataset = (getAllDatasetsQuery.data ?? []).find((d) => d.id === id);

        if (matchedDataset == null) {
            return undefined;
        }

        return {
            id: matchedDataset.id as number,
            dataCutDate: new Date(matchedDataset.dataCutDate as Date | string | number),
            created: new Date(matchedDataset.created as Date | string | number),
            isSmEnabledDataset: matchedDataset.isSmEnabledDataset ?? undefined,
        };
    }, [getLastReviewedDatasetIdForReviewPlanForDataListingForSubjectQuery.data, getAllDatasetsQuery.data]);

    const dataListingOnReview = dataListingsOnReviewIdsSet.has(selectedDatatable?.id);

    const isDataListingReview = dataListingOnReview && getReviewConfigQuery.data?.reviewSubject === false;

    const getDataListingReviewInstanceQuery = SubjectDataListingReviewInstanceApi.useGetDataListingReviewInstanceQuery(
        studyRef,
        review?.recordId,
        subjectId,
        // If not data-listing review, never set data-listing-id so that this never runs
        isDataListingReview ? selectedDatatable?.id : null,
    );
    const dataListingReviewInstance = getDataListingReviewInstanceQuery.data;

    const setDataListingReviewedMutation =
        SubjectDataListingReviewInstanceApi.useSetDataListingReviewedMutation(studyRef);

    const setDataListingReviewed = async () => {
        if (review == null || selectedDatatable == null || reviewDataset == null) {
            return;
        }

        const setToReviewed = dataListingReviewInstance?.status !== SubjectDataListingReviewStatusTypes.Reviewed;

        await setDataListingReviewedMutation.trigger({
            reviewRecordId: review.recordId,
            subjectId,
            dataListingId: selectedDatatable.id,
            dataListingName: selectedDatatable.name,
            datasetId: reviewDataset.id,
            isReviewed: setToReviewed,
            sessionId,
        } satisfies SetDataListingReviewedDto);
    };

    const getSubjectReviewInstancesQuery = SubjectReviewInstanceApi.useGetSubjects(studyRef, review?.recordId);

    const subjectOnReview = useMemo(() => {
        return (getSubjectReviewInstancesQuery.data ?? []).some((x) => x.subjectId === subjectId);
    }, [getSubjectReviewInstancesQuery.data, subjectId]);

    const handleRemovedFromReview = () => {
        smActiveReviewContext?.dispatch({ type: 'CLEAR_ACTIVE_REVIEW' });
    };

    const onSubmitHandler = () => {
        SubjectProfileService.refreshNotesGrid();
        setShowAddNoteDialog(false);
    };

    const onCloseHandler = () => {
        setShowAddNoteDialog(false);
    };

    const isLoading =
        getStudyDataTablesQuery.loading ||
        getSubjectMonitoringConfigQuery.loading ||
        getReviewQuery.isLoading ||
        getReviewConfigQuery.isLoading ||
        getDataListingReviewInstanceQuery.isLoading ||
        getSubjectReviewInstancesQuery.isLoading;
    const error =
        getStudyDataTablesQuery.error ??
        getSubjectMonitoringConfigQuery.error ??
        getReviewQuery.error ??
        getReviewConfigQuery.error ??
        getDataListingReviewInstanceQuery.error ??
        getSubjectReviewInstancesQuery.error;

    const showDataListingsGrid = !isLoading && dataListingConfigIds.length > 0 && currentDataset != null;

    // Keep track of height of grid toolbar container. It is conditionally rendered (based on `showDataListingsGrid`)
    const [toolbarHeight, setToolbarHeight] = useState(0);
    const toolbarRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        setToolbarHeight(() => toolbarRef.current?.clientHeight ?? 0);
    }, [showDataListingsGrid]);

    if (isLoading || error != null) {
        return (
            <Card
                data-testid="subject-profile-grid-loading-card"
                title={subjectMonitoringTranslation('grid.dataListings.title')}
            >
                {isLoading && error == null ? <Loading /> : <ErrorMessage error={error?.message} />}
            </Card>
        );
    }

    return (
        <>
            {showAddNoteDialog && subjectCode && subjectNumber && subjectId && sessionId !== undefined && review && (
                <AddSubjectNoteDialog
                    sessionId={sessionId}
                    studyRef={studyRef}
                    subjectId={subjectId}
                    subjectNumber={subjectNumber}
                    datasetId={dataset.id}
                    datasetOpenedOn={dataset.dataCutDate}
                    reviewId={review.id!}
                    reviewTitle={review.title!}
                    reviewDatasetDate={review.datasetDate!}
                    associationName="Subject Profile"
                    associationType={1}
                    dataListingName={selectedDatatable?.name ?? null}
                    dataListingRef={selectedDatatable?.id ?? null}
                    onClose={onCloseHandler}
                    onSubmit={onSubmitHandler}
                    onRemovedFromReview={handleRemovedFromReview}
                    panelName={null}
                    panelRef={null}
                />
            )}

            {showDataListingsGrid ? (
                <div className="k-h-full">
                    <div className={style.addNoteToolbar} data-testid="data-listing-add-note-toolbar" ref={toolbarRef}>
                        {isDataListingReview && subjectOnReview && (
                            <Button
                                data-testid="set-datalisting-reviewed-button"
                                primary
                                onClick={setDataListingReviewed}
                                disabled={setDataListingReviewedMutation.isMutating}
                            >
                                {dataListingReviewInstance?.status === SubjectDataListingReviewStatusTypes.Reviewed
                                    ? subjectMonitoringTranslation('subjectProfile.dataListings.unmarkAsReviewed')
                                    : subjectMonitoringTranslation('subjectProfile.dataListings.markAsReviewed')}
                            </Button>
                        )}
                        <Button
                            data-testid="data-listing-add-note-button"
                            primary
                            onClick={() => setShowAddNoteDialog(true)}
                            disabled={!isInReviewMode}
                        >
                            {subjectMonitoringTranslation('subjectProfile.dataListings.addNote')}
                        </Button>
                    </div>

                    <div style={{ height: `calc(100% - ${toolbarHeight}px)` }}>
                        <SubjectMonitoringDataTables
                            // Reset when data-table changes, active review mode is entered/exited, or subject changes
                            key={`${selectedDatatable?.id}-${review?.recordId}-${subjectNumber}`}
                            datatables={availableDataTables}
                            datatablesGroupByField={isInReviewMode ? GROUPED_BY_KEY : undefined}
                            selectedDatatable={selectedDatatable}
                            setSelectedDatatable={setSelectedDatatable}
                            studyRef={studyRef}
                            studyNumber={studyNumber}
                            currentDataset={currentDataset!}
                            reviewDataset={reviewDataset}
                            lastReviewedDataset={lastReviewedDataset}
                            subjectId={subjectId}
                            subjectNumber={subjectNumber}
                            // Only pass active review (to load Data-Listing-Review grid) if selected data-listing is part of
                            //   the Review Plan of the active review
                            activeReview={dataListingOnReview ? review : undefined}
                            noPadding
                        />
                    </div>
                </div>
            ) : (
                <div className={style.noDataViewConfigWrapper}>
                    <div className={style.noDataViewConfig}>
                        <InfoField
                            type="Info"
                            message={centralMonitoringTranslation('dataView.datatables.noDataTable')}
                        />
                    </div>
                </div>
            )}
        </>
    );
};

export default DataListing;
